/**
 * The front door.
 *
 * Built to the mockup: a wordmark bar, a hero over the photographed felt, the
 * four things worth telling a stranger, and the games themselves.
 *
 * ## What the page is for, in order
 *
 * 1. **Somebody holding a four-character code.** Still the most common arrival,
 *    and still first: "Join with a Code" is one of the two hero actions and
 *    reveals its own input in place (see `JoinWithCode`). D2's constraint —
 *    that a code-holder should not have to look past anything — is met by
 *    position rather than by a permanent form.
 * 2. **Somebody who wants to start one.** The gold action, straight to the
 *    library, which is step one of the flow F8 settled.
 * 3. **A stranger with neither.** Everything below the fold.
 */

import { Link } from 'react-router-dom';
import { hasAccount, useSession } from '../session-context.js';
import { useCatalog } from '../use-catalog.js';
import { Icon } from '../render/Icon.js';
import { GameCard } from './GameCard.js';
import { JoinWithCode } from './JoinWithCode.js';

/**
 * How many games the strip shows.
 *
 * Six is what the mockup draws and it divides evenly into two and three
 * columns, so the grid never ends with one orphan on a row at any width we
 * support.
 */
const POPULAR = 6;

export function Landing() {
  const state = useSession();
  const signedIn = hasAccount(state);
  const { catalog } = useCatalog();

  /*
   * Free games first.
   *
   * The strip is the answer to "what can I play", asked by somebody who has
   * bought nothing. Leading with something they would have to pay for answers
   * a question they did not ask.
   */
  const popular = (catalog?.games ?? [])
    .slice()
    .sort((a, b) => Number(b.free) - Number(a.free))
    .slice(0, POPULAR);

  return (
    <div className="page landing">
      <header className="landing-bar">
        {/*
          The lockup, not the letters (A3).

          `alt` is the spoken name rather than the written one: read literally,
          R3V3L is "R-three-V-three-L", which is not what anybody calls it. The
          digits are a drawing of the name, so what is drawn stays in the
          picture and what is said goes in the alt text.
        */}
        <img
          className="landing-logo"
          src="/art/logo-small.webp"
          width={320}
          height={97}
          alt="Revelry"
        />

        {/*
          Two, and both are real pages.

          The mockup offers four - Games, How It Works, Features, Play Anywhere
          - and three of those are pages we do not have. "How it works" was here
          as an anchor to a section of this page, and that section has since
          been replaced by the game-night card, so the anchor went with it
          rather than being left pointing at whatever now sits at that id.
        */}
        <nav className="landing-links" aria-label="Main">
          <Link to="/library">Games</Link>
          <Link to="/store">Store</Link>
        </nav>

        {/*
          One button at the right, as the mockup draws it.

          Log out has gone from here. It was the same weight as "Account"
          despite being nobody's reason for visiting, and the account page
          already carries it - which is where somebody looking for it would
          go anyway.
        */}
        <div className="landing-account">
          {signedIn ? (
            <Link className="pill-button" to="/account">
              Account
            </Link>
          ) : (
            <>
              <Link className="text-link" to="/login">
                Sign in
              </Link>
              <Link className="pill-button" to="/register">
                Create account
              </Link>
            </>
          )}
        </div>
      </header>

      <section className="landing-hero">
        <h1 className="hero-title">
          Card Games.
          <br />
          {/* The second line in gold, as drawn. A span rather than two headings:
              it is one sentence and a screen reader should read it as one. */}
          <span className="hero-title-gold">Closer People.</span>
        </h1>

        <p className="hero-blurb">
          Play your favorite card games with friends and family from anywhere.
          Talk, chat, laugh, and keep the good games going.
        </p>

        <div className="hero-actions">
          {/* The label is its own element so the grid in `.cta` can center it
              between the two icon columns rather than centering icons-plus-text
              as one lump. */}
          <Link className="cta cta-primary" to="/library">
            {/* A spade, not a play triangle: this opens a card table rather
                than starting a video, and the suit says so faster. */}
            <Icon name="spade" className="cta-mark" />
            <span className="cta-label">Start a Game</span>
            <Icon name="chevron-right" />
          </Link>
          <JoinWithCode />
        </div>
      </section>

      {!state.session ? (
        <p className="banner warn">
          Can&apos;t reach the server. Check it is running, then reload.
        </p>
      ) : null}

      {/*
        The mockup's four.

        These replace three facts about the product (no app, no sign-up, your
        hand is private) with four claims about the experience. The trade is
        deliberate: the facts were checkable and dry, and these say what it is
        *for*.

        "Voice & Chat" is honest as of 2026-09-17, when voice was switched on
        in production. It was held back until then — a front page advertising a
        feature that is dark is the sequencing problem the catalog flagged.
      */}
      <ul className="value-props">
        {/*
          The badge is a wrapper rather than the icon itself, and it has to be.
          A masked element is clipped to its glyph, so a disc painted on the
          icon is cut away with everything else - the circle needs an element
          of its own behind it.
        */}
        <li>
          <span className="prop-badge">
            <Icon name="mic" />
          </span>
          <strong>Voice &amp; Chat</strong>
          <span className="prop-note">Feel like you&apos;re at the same table</span>
        </li>
        <li>
          <span className="prop-badge">
            <Icon name="people" />
          </span>
          <strong>Play Together</strong>
          <span className="prop-note">With friends and family</span>
        </li>
        <li>
          <span className="prop-badge">
            <Icon name="gamepad" />
          </span>
          <strong>Great Games</strong>
          <span className="prop-note">Classic favorites</span>
        </li>
        <li>
          <span className="prop-badge">
            <Icon name="heart" />
          </span>
          <strong>Anytime, Anywhere</strong>
          <span className="prop-note">On web or mobile</span>
        </li>
      </ul>

      <section className="popular-games">
        <div className="popular-head">
          <h2 className="popular-title">Popular Games</h2>
          <Link className="see-all" to="/library">
            See all
            <Icon name="chevron-right" />
          </Link>
        </div>

        {popular.length > 0 ? (
          <div className="popular-strip">
            {popular.map((game) => (
              <GameCard key={game.id} game={game} compact />
            ))}
          </div>
        ) : (
          // The catalog is fetched per session and a guest may not have it
          // yet. A quiet gap beats a row of skeletons on a front page.
          <p className="field-help">Loading the games…</p>
        )}
      </section>

      {/*
        Replaces the three-step "How it works".

        That section explained the mechanics to somebody who had not asked;
        this one describes the evening, which is what the page is selling. The
        steps are not lost — they are what the invite and configure screens
        walk a host through, in order, at the moment it matters.
      */}
      <section className="promo-card">
        <span className="promo-card-badge">
          <Icon name="people" />
        </span>
        <div className="promo-card-text">
          <h2 className="promo-card-title">Make It a Game Night</h2>
          <p className="promo-card-note">
            Open a private table, invite your people, and play all evening.
          </p>
        </div>
        {/* The library, because "what could we play all evening" is a question
            the game list answers and no other page does. */}
        <Link className="pill-button" to="/library">
          Learn More
        </Link>
      </section>

      <footer className="landing-foot">
        <img
          className="landing-foot-mark"
          src="/art/logo-small.webp"
          width={320}
          height={97}
          alt="Revelry"
        />
        <span className="landing-foot-line">Good cards. Better company.</span>
      </footer>
    </div>
  );
}
